import { Helmet } from "react-helmet-async";
import { business } from "../content/business";

type SEOProps = {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

const SITE = "https://nexirt.com";
// Rev 26.1 — refreshed OG image generated via Playwright screenshot
// of /og-preview/ (NEXIRT robot + paper-graph bg + "Powered by AI.
// Run by humans." tagline). The Session 0 placeholder
// /assets/img/og-image.jpg stays on disk for back-compat but is no
// longer the default. Re-run apps/web/scripts/capture-og.ts to refresh.
const DEFAULT_IMG = `${SITE}/og-image-v2.png`;

export default function SEO({
  title,
  description,
  path,
  image = DEFAULT_IMG,
  type = "website",
  jsonLd,
}: SEOProps) {
  // Phase 13 Loop 1 — if running on a tenant subdomain, the canonical
  // + JSON-LD identity use the tenant's name + their own URL. NEXIRT's
  // Organization JSON-LD only appears on the apex marketing site.
  const tenant = typeof window !== "undefined" ? window.__nexirt_tenant : undefined;
  const onTenantSubdomain = typeof window !== "undefined" && window.location.hostname.endsWith(".app.nexirt.com");
  const siteRoot = onTenantSubdomain ? `https://${window.location.hostname}` : SITE;
  const canonical = `${siteRoot}${path}`;
  const organizationLd = onTenantSubdomain && tenant ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: tenant.name,
    url: siteRoot,
  } : {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE}/#organization`,
    name: business.name,
    legalName: business.legalName,
    url: SITE,
    logo: `${SITE}/logo-apex-primary.svg`,
    description: "Custom software development studio and AI agent workflow integration partner. Portals, dashboards, payroll workflows, mapping platforms, marketing sites, and bespoke business systems.",
    slogan: "Powered by AI. Run by humans.",
    email: business.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: business.address.street,
      addressLocality: business.address.city,
      addressRegion: business.address.region,
      postalCode: business.address.postalCode,
      addressCountry: business.address.country,
    },
    foundingDate: business.founded,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: business.contact.email,
        availableLanguage: ["en"],
        areaServed: "Worldwide",
      },
    ],
    // Keep in sync with the footer social icons (Layout.tsx) and the
    // static graph in index.html.
    sameAs: [
      "https://www.linkedin.com/company/nexirt",
      "https://www.instagram.com/nexirt_com/",
      "https://github.com/NewtonCazzaro",
    ],
  };
  const lds = [organizationLd, ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content="NEXIRT" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {lds.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}
